import {
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GLOBAL_SEARCH_GROUP_ORDER,
} from "@frontend-shared/constants";
import {
  EGlobalSearchItemType,
  type IGlobalSearchGroup,
} from "@frontend-shared/types";
import { sortGlobalSearchGroups } from "@frontend-shared/utils";
import { useMemo } from "react";

import { useDebounce } from "@/hooks/use-debounce";

import type {
  IGlobalSearchChatsState,
  IGlobalSearchServerGroupsResult,
} from "../types";
import { useGlobalSearchChats } from "./use-global-search-chats";
import { useGlobalSearchClientGroups } from "./use-global-search-client-groups";
import { useGlobalSearchServerGroups } from "./use-global-search-server-groups";

const CHAT_RANK = GLOBAL_SEARCH_GROUP_ORDER.indexOf(EGlobalSearchItemType.CHAT);

const rankOf = (type: EGlobalSearchItemType): number => {
  const rank = GLOBAL_SEARCH_GROUP_ORDER.indexOf(type);
  return rank === -1 ? GLOBAL_SEARCH_GROUP_ORDER.length : rank;
};

interface IUseGlobalSearchResultsProps {
  query: string;
  onClose: () => void;
}

interface IGlobalSearchResults {
  // NOTE: Groups ranked before the chat sections (pages, settings) and after
  // them (everything else), in GLOBAL_SEARCH_GROUP_ORDER.
  leadingGroups: IGlobalSearchGroup[];
  trailingGroups: IGlobalSearchGroup[];
  chats: IGlobalSearchChatsState;
  server: IGlobalSearchServerGroupsResult;
  isSearching: boolean;
  // NOTE: Something besides the chats matched. The fallback actions do not
  // count — they are offered for every query, so they would otherwise hide
  // the "nothing found" state for good.
  hasOtherResults: boolean;
}

// NOTE: NSAI-6589 — every source of the palette merged into one ordered list.
// The chat sections are not IGlobalSearchItem groups (they keep their own
// rows, paging and states), so the list is split around the CHAT slot.
export const useGlobalSearchResults = ({
  query,
  onClose,
}: IUseGlobalSearchResultsProps): IGlobalSearchResults => {
  const debouncedQuery = useDebounce(query, GLOBAL_SEARCH_DEBOUNCE_MS);
  const chats = useGlobalSearchChats({ query, debouncedQuery, onClose });
  const clientGroups = useGlobalSearchClientGroups({ query, isEnabled: true });
  const server = useGlobalSearchServerGroups({ debouncedQuery });

  const { leadingGroups, trailingGroups, hasOtherResults } = useMemo(() => {
    const groups = sortGlobalSearchGroups([...clientGroups, ...server.groups]);
    return {
      leadingGroups: groups.filter((group) => rankOf(group.type) < CHAT_RANK),
      trailingGroups: groups.filter((group) => rankOf(group.type) > CHAT_RANK),
      hasOtherResults: groups.some(
        (group) => group.type !== EGlobalSearchItemType.ACTION,
      ),
    };
  }, [clientGroups, server.groups]);

  return {
    leadingGroups,
    trailingGroups,
    chats,
    server,
    isSearching: query.trim().length > 0,
    hasOtherResults,
  };
};
